import { memo } from "react";
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Tag,
  TagLabel,
  Text,
  Wrap,
  useDisclosure,
} from "@chakra-ui/react";
import TagLink from "components/common/link/tag-link";

export interface VirtualizedTableTag {
  id: string;
  name: string;
  href?: string;
  onClick?: () => void;
}

interface VirtualizedTableTagCollectionProps {
  tags: VirtualizedTableTag[];
  placeholder?: string;
  colorScheme?: string;
}

const VirtualizedTableTagCollection = ({
  tags = [],
  placeholder = "--",
  colorScheme = "slate",
}: VirtualizedTableTagCollectionProps) => {
  const { onOpen, onClose, isOpen } = useDisclosure();

  const getBody = () => {
    if (tags && !tags.length) {
      return <Tag colorScheme="none">{placeholder}</Tag>;
    }

    if (tags && !tags[0].href) {
      return (
        <Tag colorScheme={colorScheme}>
          <TagLabel>{tags[0]?.name}</TagLabel>
        </Tag>
      );
    }

    return (
      <TagLink
        href={tags ? tags[0].href : null}
        colorScheme={colorScheme}
        onClick={tags ? tags[0].onClick : null}
      >
        {tags ? tags[0].name : null}
      </TagLink>
    );
  };

  return (
    <Popover
      preventOverflow={false}
      eventListeners={{ scroll: false, resize: false }}
      isLazy
      placement="bottom-start"
      trigger="hover"
      computePositionOnMount={false}
    >
      <PopoverTrigger>
        <Stack
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onFocus={onOpen}
          onBlur={onClose}
          direction="row"
          position="relative"
          cursor="pointer"
        >
          {getBody()}
          {tags && tags.length > 1 && (
            <Text color="gray.500" size="sm">
              +{tags.length - 1} more
            </Text>
          )}
        </Stack>
      </PopoverTrigger>
      {tags && !!tags.length && isOpen && (
        <Portal>
          <PopoverContent
            position="relative"
            onMouseEnter={onOpen}
            onMouseLeave={onClose}
            _after={{
              content: "''",
              position: "absolute",
              w: "100%",
              h: "8px",
              top: "-8px",
              left: 0,
            }}
          >
            <PopoverBody overflowY="auto" maxH="210px">
              <Wrap>
                {tags.map((tag) =>
                  tag.href ? (
                    <TagLink
                      key={tag.id}
                      href={tag.href}
                      colorScheme={colorScheme}
                      onClick={tags[0].onClick}
                    >
                      key={tag.id} colorScheme={colorScheme}
                    </TagLink>
                  ) : (
                    <Tag key={tag.id} colorScheme={colorScheme}>
                      {tag.name}
                    </Tag>
                  )
                )}
              </Wrap>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      )}
    </Popover>
  );
};

export default memo(VirtualizedTableTagCollection);
